/**
 * Placeholder rendering of the "GKV - Zertifikat" that highlights where the
 * Garantienummer is printed. The logo is a placeholder box and all personal
 * data is rendered as skeleton bars — only the labels and the highlighted
 * certificate number are real.
 *
 * The sheet keeps the A4 portrait ratio and is capped at a readable width. It
 * is a query container, so every size below is expressed in `cqw` (percent of
 * the sheet's own width): the whole document scales as one, which keeps the
 * proportions identical from a 320px phone up to the capped width, without a
 * separate mobile layout. Content deliberately runs past the bottom edge and is
 * cropped, so the fade reads as "the document continues".
 */

import type { ReactNode } from "react";

/** Skeleton stand-in for a value that would hold personal data. */
function SkeletonValue({ className }: { className: string }) {
  return <div className={`h-[1.9cqw] rounded-[0.4cqw] bg-neutral-100 ${className}`} />;
}

function Field({ label, valueWidth }: { label: string; valueWidth: string }) {
  return (
    <div className={"flex items-center gap-[2cqw]"}>
      <div className={"w-[18cqw] shrink-0 font-bold"}>{label}</div>
      <SkeletonValue className={valueWidth} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={"flex flex-col gap-[2cqw]"}>
      <div className={"text-[3.2cqw] font-bold"}>{title}</div>
      <div className={"grid grid-cols-2 gap-x-[4cqw] gap-y-[1.8cqw]"}>{children}</div>
    </div>
  );
}

export function ContractCertificate() {
  return (
    <div
      className={
        "relative mx-auto aspect-[210/297] w-full max-w-[22rem] overflow-hidden rounded border border-gray-200 bg-white [container-type:inline-size]"
      }
      aria-hidden={"true"}
    >
      <div className={"flex flex-col gap-[4cqw] p-[5cqw] text-[2.9cqw] leading-tight"}>
        <div className={"flex items-start justify-between gap-[3cqw]"}>
          {/* Placeholder for the mobile Garantie logo. */}
          <div
            className={
              "flex h-[13cqw] w-[32cqw] shrink-0 items-center justify-center rounded-[0.8cqw] text-[2.6cqw] text-neutral-500"
            }
          >
            <img src="/images/brand/mobile-garantie.svg" alt="mobile GARANTIE" />
          </div>

          <div
            className={"flex items-start gap-[2cqw] rounded-[1cqw] border border-gray-200 p-[2cqw]"}
          >
            <div className={"h-[11cqw] w-[11cqw] shrink-0 rounded-[0.4cqw] bg-neutral-300"} />
            <div>
              <div className={"text-[2.8cqw] font-bold"}>Versicherungsbedingungen</div>
              <div className={"text-[2.4cqw] text-neutral-500"}>
                Die Smartphone-Kamera verwenden um Dokumente abzurufen.
              </div>
            </div>
          </div>
        </div>

        {/* This is what the driver is looking for. */}
        <div
          className={
            "self-start border-[0.6cqw] border-error-500 px-[2.5cqw] py-[1.5cqw] text-[3.8cqw] font-bold"
          }
        >
          GKV - Zertifikat Nr. H01-123123
        </div>

        <Section title={"Versicherungsnehmer (Händler)"}>
          <div className={"flex flex-col gap-[1.8cqw]"}>
            <Field label={"Firma"} valueWidth={"w-[24cqw]"} />
            <Field label={"Zusatz"} valueWidth={"w-[20cqw]"} />
            <Field label={"Straße"} valueWidth={"w-[22cqw]"} />
            <Field label={"PLZ, Ort"} valueWidth={"w-[19cqw]"} />
            <Field label={"Land"} valueWidth={"w-[16cqw]"} />
          </div>
          <div className={"flex flex-col gap-[1.8cqw]"}>
            <Field label={"Telefon"} valueWidth={"w-[22cqw]"} />
            <Field label={"Mobil"} valueWidth={"w-[19cqw]"} />
            <Field label={"E-Mail"} valueWidth={"w-[24cqw]"} />
          </div>
        </Section>

        <Section title={"Fahrzeugdaten"}>
          <div className={"flex flex-col gap-[1.8cqw]"}>
            <Field label={"Kennzeichen"} valueWidth={"w-[19cqw]"} />
            <Field label={"Marke"} valueWidth={"w-[16cqw]"} />
            <Field label={"FIN"} valueWidth={"w-[24cqw]"} />
            <Field label={"Leistung"} valueWidth={"w-[14cqw]"} />
            <Field label={"Laufleistung"} valueWidth={"w-[17cqw]"} />
          </div>
          <div className={"flex flex-col gap-[1.8cqw]"}>
            <Field label={"Erstzulassung"} valueWidth={"w-[17cqw]"} />
            <Field label={"Modell"} valueWidth={"w-[24cqw]"} />
            <Field label={"Getriebe"} valueWidth={"w-[15cqw]"} />
            <Field label={"Kraftstoff"} valueWidth={"w-[16cqw]"} />
            <Field label={"Hubraum"} valueWidth={"w-[14cqw]"} />
          </div>
        </Section>

        <Section title={"Vertragsdaten"}>
          <div className={"flex flex-col gap-[1.8cqw]"}>
            <Field label={"Tarif"} valueWidth={"w-[20cqw]"} />
            <Field label={"Laufzeit"} valueWidth={"w-[17cqw]"} />
            <Field label={"Beginn"} valueWidth={"w-[16cqw]"} />
          </div>
          <div className={"flex flex-col gap-[1.8cqw]"}>
            <Field label={"Ablauf"} valueWidth={"w-[16cqw]"} />
            <Field label={"Beitrag"} valueWidth={"w-[15cqw]"} />
            <Field label={"Zahlweise"} valueWidth={"w-[18cqw]"} />
          </div>
        </Section>
      </div>
    </div>
  );
}
